import { auth } from '@/auth/firebase';
import { clearImpersonation, getLocalStorageImpersonatedAsUserId } from '@/auth/impersonation';
import { ImpersonatingAsHeader, reviver } from '@airreading/common';
import axios, { AxiosError, type AxiosInstance, type AxiosResponseTransformer } from 'axios';
import * as Sentry from '@sentry/react';
import { getCsrfTokenForAxios, setCsrfTokenForAxios } from './csrf-utils';
import { signOut } from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { type RegisteredRouter } from '@tanstack/react-router';

type RegisteredChildrenRouter = RegisteredRouter['routeTree']['types']['children']['LayoutRoute']['types']['children'];
type App403RoutePath = RegisteredChildrenRouter['Layout403Route']['path'];

/**
 * Until https://github.com/axios/axios/issues/5924 is fixed, we need an entire
 * `transformResponse` to customize the `JSON.parse` reviver function.
 */
const transformResponse: AxiosResponseTransformer = function (data, headers) {
  const contentType = headers['content-type'] as string;
  const isJSON = contentType.startsWith('application/json');
  if (data && Object.prototype.toString.call(data) === '[object String]' && isJSON) {
    try {
      return JSON.parse(data, reviver);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (e.name === 'SyntaxError') {
        throw AxiosError.from(e, AxiosError.ERR_BAD_RESPONSE, this, null);
      }
      throw e;
    }
  }

  return data;
};

export const axiosClient: AxiosInstance = (() => {
  const getAuthToken = async (): Promise<string> => {
    if (!auth.currentUser) {
      return '';
    }

    try {
      return 'Bearer ' + (await auth.currentUser.getIdToken());
    } catch (err) {
      Sentry.captureException(err, {
        tags: {
          errorType:
            err instanceof FirebaseError && typeof err.code === 'string' && err.code.startsWith('auth/')
              ? (err.code as string)
              : 'auth_error',
        },
        contexts: {
          app: {
            component: 'axiosClient',
            route: window.location.pathname,
            userId: auth.currentUser?.uid || 'unknown',
          },
        },
        extra: {
          axiosError: false,
          fullError: err,
        },
      });

      clearImpersonation({ reload: true });
      setCsrfTokenForAxios(null);
      await signOut(auth);

      return Promise.reject(err);
    }
  };

  const instance = axios.create({
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000/',
    paramsSerializer: {
      indexes: null,
    },
    transformResponse,
  });

  const impersonatedUserId = getLocalStorageImpersonatedAsUserId();

  instance.interceptors.request.use(async (config) => {
    const authHeader = await getAuthToken();
    if (authHeader) {
      config.headers.Authorization = authHeader;
    }
    if (impersonatedUserId) {
      config.headers[ImpersonatingAsHeader] = impersonatedUserId;
    }

    const csrfToken = getCsrfTokenForAxios();
    if (csrfToken) {
      config.headers['x-csrf-token'] = csrfToken;
    }

    config.withCredentials = true;
    return config;
  });

  // Response interceptor to catch and log server errors to Sentry
  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      // Only log server errors (4xx and 5xx status codes)
      if (error.response && error.response.status >= 400) {
        const { status, statusText, data } = error.response;
        const { method, url, baseURL } = error.config || {};

        // Create a structured error context for Sentry
        const errorContext = {
          status,
          statusText,
          method: method?.toUpperCase(),
          url: baseURL ? `${baseURL}${url}` : url,
          responseData: data,
          requestHeaders: error.config?.headers,
          timestamp: new Date().toISOString(),
        };

        // Log to Sentry with additional context
        Sentry.captureException(error, {
          tags: {
            errorType: 'server_error',
            httpStatus: status,
            httpMethod: method?.toUpperCase(),
          },
          contexts: {
            http: errorContext,
          },
          extra: {
            axiosError: true,
            fullError: error,
          },
        });

        //Redirect to /403 page
        if (error.response.status === 403) {
          const url403: App403RoutePath = '/403';
          const is403 = window.location.pathname.startsWith(url403);

          if (!is403) {
            window.location.replace(url403);
          }
        }
      }

      // Re-throw the error so it can still be handled by the calling code
      return Promise.reject(error);
    },
  );

  return instance;
})();
